/**
 * FI Notification Inbox API Service (GH #2281)
 *
 * Type-safe client for the institutional notification inbox -- the read
 * surface over the fi_officer/fi_ops Notification rows the outbox handler
 * writes. Backend lives in platform-admin-api at /api/v1/notifications
 * (notification-inbox.routes.ts).
 *
 * Types derived from notification-inbox.openapi.yaml -- no manual interfaces.
 *
 * Note on baseUrl: unlike platform-admin.openapi.yaml (whose Task paths omit
 * the /api prefix, so tasks.ts anchors its client at `/api`), the
 * notification-inbox spec authors FULL `/api/v1/notifications/...` path keys.
 * The client therefore anchors at '' so path keys produce the runtime URL
 * verbatim.
 */

import createClient from 'openapi-fetch';
import type { paths as NotificationInboxPaths } from '@aaimplatform/institutional-types/notification-inbox';
import type { components } from '@aaimplatform/institutional-types/notification-inbox';
import { authMiddleware } from '../client';

// ---------------------------------------------------------------------------
// Type aliases from generated OpenAPI schema
// ---------------------------------------------------------------------------

export type InboxNotification = components['schemas']['Notification'];
export type NotificationListResponse = components['schemas']['NotificationListResponse'];
export type NotificationResponse = components['schemas']['NotificationResponse'];
export type MarkAllReadResponse = components['schemas']['MarkAllReadResponse'];
export type NotificationEventType = components['schemas']['NotificationEventType'];
export type NotificationSeverity = components['schemas']['NotificationSeverity'];

// ---------------------------------------------------------------------------
// Dedicated openapi-fetch client (full-path keys; see baseUrl note above)
// ---------------------------------------------------------------------------

const notificationsClient = createClient<NotificationInboxPaths>({
  baseUrl: '',
  credentials: 'include',
});
notificationsClient.use(authMiddleware);

// ---------------------------------------------------------------------------
// Query parameters
// ---------------------------------------------------------------------------

export interface NotificationListQueryOptions {
  eventType?: NotificationEventType;
  severity?: NotificationSeverity;
  unreadOnly?: boolean;
  pageSize?: number;
  cursor?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || result.data === undefined) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const body = result.error as { error?: { message?: string } } | undefined;
    throw new Error(body?.error?.message ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const notificationsApi = {
  list: async (params?: NotificationListQueryOptions): Promise<NotificationListResponse> => {
    const result = await notificationsClient.GET('/api/v1/notifications', {
      params: {
        query: {
          eventType: params?.eventType,
          severity: params?.severity,
          unreadOnly: params?.unreadOnly,
          pageSize: params?.pageSize,
          cursor: params?.cursor,
        },
      },
    });
    return unwrap(result);
  },

  get: async (notificationId: string): Promise<NotificationResponse> => {
    const result = await notificationsClient.GET('/api/v1/notifications/{notificationId}', {
      params: { path: { notificationId } },
    });
    return unwrap(result);
  },

  markRead: async (notificationId: string): Promise<NotificationResponse> => {
    const result = await notificationsClient.POST('/api/v1/notifications/{notificationId}/read', {
      params: { path: { notificationId } },
    });
    return unwrap(result);
  },

  markAllRead: async (): Promise<MarkAllReadResponse> => {
    const result = await notificationsClient.POST('/api/v1/notifications/read-all');
    return unwrap(result);
  },
};
